import { Tabs, Button, Modal, Tag } from "antd";
import React, { useState, useEffect } from "react";
import PageHeader from "app/shared/PageHeader";
import "./HotelMailFollowupConfig.scss";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { MailOutlined, FileTextOutlined } from "@ant-design/icons";
import Typography from "app/shared/Typography";
import InformationFlowTab from "./InformationFlowTab";
import ColumnMappingTab from "./ColumnMappingTab";
import EmailFollowupTab, {
  FollowupType,
  EmailFollowupData,
} from "./EmailFollowupTab";

const { TabPane } = Tabs;

interface DatasetInfo {
  datasourceType?: string;
  [key: string]: any;
}

interface EmailFlow {
  id: number;
  type: string;
  interval: string;
  name: string;
  time: string;
  templateId: string;
  intervalAgainst: string;
}

interface ColumnMapping {
  id: number;
  sourceField: string; // fixed (uneditable if predefined)
  targetField: string;
  isPredefined?: boolean;
  isRequired?: boolean;
}

const DB_TYPE_MAP: Record<string, string> = {
  MONGO: "mongo",
  POSTGRE: "postgres",
};

// const createEmptyFollowupData = (): EmailFollowupData => ({
//   entries: [
//     {
//       id: 1,
//       name: "",
//       interval: "0",
//       time: "00:00",
//       intervalAgainst: "",
//       templateId: "",
//       subject: "",
//       logoUrls: [],
//     },
//   ],
//   bulkEmailKey: "",
//   attachmentKeys: [],
//   attachmentKeyLabels: {},
//   from: "",
//   cc: "",
// });

const getMappedColumnsFromConfig = (config: any): string[] => {
  const mappedColumns = Object.values(config?.mapping || {})
    .flatMap((value: any) => (Array.isArray(value) ? value : [value]))
    .filter(
      (value): value is string =>
        typeof value === "string" &&
        value.trim().length > 0 &&
        value !== "null" &&
        value !== "Null",
    );

  const dynamicColumns = Array.isArray(config?.dynamicColumns)
    ? config.dynamicColumns
        .map((column: any) => column?.key)
        .filter(
          (value: any): value is string =>
            typeof value === "string" && value.trim().length > 0,
        )
    : [];

  return Array.from(new Set([...mappedColumns, ...dynamicColumns]));
};

const HotelEmailConfigSetup: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("config");
  const navigate = useNavigate();
  const [editId, setEditId] = useState<string | null>(null);
  const sourceFieldPlaceholders: Record<string, string> = {
    email_bookingId: "Invoice / Booking Number",
    email_sharableBookingId: "Shareable Invoice / Booking Number",
    email_checkindate: "Check-In Date",
    email_bookingdate: "Booking Date",
    email_checkoutdate: "Invoice Date / Check-Out Date",
    company_name: "Client Name",
    gstin: "Client GSTIN",
    company_address: "Company Address",
    totalGST: "Total GST Amount",
    email_hotel_name: "Hotel Name",
    email_hotel_gstin: "Hotel GSTIN",
  };

  const INVOICE_MAPPINGS: ColumnMapping[] = [
    {
      id: 1,
      sourceField: "email_bookingId",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 2,
      sourceField: "email_sharableBookingId",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 3,
      sourceField: "email_checkindate",
      targetField: "",
      isPredefined: true,
      isRequired: false,
    },
    {
      id: 4,
      sourceField: "email_bookingdate",
      targetField: "",
      isPredefined: true,
      isRequired: false,
    },
    {
      id: 5,
      sourceField: "email_checkoutdate",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 6,
      sourceField: "company_name",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 7,
      sourceField: "gstin",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 8,
      sourceField: "company_address",
      targetField: "",
      isPredefined: true,
      isRequired: false,
    },
    {
      id: 9,
      sourceField: "totalGST",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 10,
      sourceField: "email_hotel_name",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 11,
      sourceField: "email_hotel_gstin",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
  ];

  const GST_MAPPINGS: ColumnMapping[] = [
    {
      id: 1,
      sourceField: "email_bookingId",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 2,
      sourceField: "email_sharableBookingId",
      targetField: "",
      isPredefined: true,
      isRequired: false,
    },
    {
      id: 3,
      sourceField: "email_hotel_name",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 4,
      sourceField: "email_checkindate",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 5,
      sourceField: "email_bookingdate",
      targetField: "",
      isPredefined: true,
      isRequired: false,
    },
    {
      id: 6,
      sourceField: "email_checkoutdate",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 7,
      sourceField: "company_name",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 8,
      sourceField: "gstin",
      targetField: "",
      isPredefined: true,
      isRequired: true,
    },
    {
      id: 9,
      sourceField: "company_address",
      targetField: "",
      isPredefined: true,
      isRequired: false,
    },
  ];

  const [mappings, setMappings] = useState<ColumnMapping[]>(INVOICE_MAPPINGS);

  const [basicInfo, setBasicInfo] = useState({
    name: "",
    client_id: "",
    fromEmail: "",
    fromName: "",
    datasourceType: "MANUAL", // default
    databaseType: "", // default
    database: "unified-hotel-followup",
    collection: "",
    mailType: "SINGLE", // default
    bookingid: "",
    subject: "",
    safety_valve: {
      key: "",
      value: "",
    },
    gstconfig: {
      email_hotel_gstin: "",
      gstkey: "",
    },
    searchPattern: "",
    gptContext: "",
  });

  const [configClientType, setConfigClientType] = useState<string>("");
  const [configWorkspaceName, setConfigWorkspaceName] = useState<string>("");
  const [configWorkspaceId, setConfigWorkspaceId] = useState<string>("");
  const [configTmcDomain, setConfigTmcDomain] = useState<string>("");

  const [sourceType, setSourceType] = useState<"DATABASE" | "UPLOAD" | null>(
    null,
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileInfo, setUploadedFileInfo] = useState<any>(null);
  const [uploadedFileColumns, setUploadedFileColumns] = useState<string[]>([]);
  const [bookingIds, setBookingIds] = useState<string[]>([]);
  const [followupType, setFollowupType] = useState<FollowupType>(null);
  // const [followupData, setFollowupData] = useState<EmailFollowupData>(
  //   createEmptyFollowupData(),
  // );
  const [followupData, setFollowupData] = useState<EmailFollowupData>({
    entries: [{ id: 1, name: "", interval: "0", time: "00:00", intervalAgainst: "", templateId: "", subject: "", logoUrls: [] }],
    bulkEmailKey: "",
    attachmentKeys: [],
    attachmentKeyLabels: {},
    from: "",
    fromName: "",
    cc: "",
  });

  const [flows, setFlows] = useState<EmailFlow[]>([
    {
      id: 1,
      type: "EMAIL",
      interval: "",
      name: "",
      time: "",
      templateId: "",
      intervalAgainst: "",
    },
  ]);

  const [action, setAction] = useState("create");
  const isEditMode = action === "edit" || location.state?.action === "edit";

  const [reuploadModalVisible, setReuploadModalVisible] = useState(false);
  const [reuploadPendingFile, setReuploadPendingFile] = useState<File | null>(null);
  const [reuploadNewCols, setReuploadNewCols] = useState<string[]>([]);
  const [reuploadExistingCols, setReuploadExistingCols] = useState<string[]>([]);

  useEffect(() => {
    if (
      location.state &&
      location.state.action === "edit" &&
      location.state.data
    ) {
      const receivedData = location.state.data;
      setAction("edit");
      setActiveTab("config");

      setEditId(receivedData._id || null);
      // Detect source type: invoice always uses email_followup_automation DB = UPLOAD
      setSourceType(receivedData.database === "email_followup_automation" ? "UPLOAD" : "DATABASE");
      setUploadedFile(null);
      setUploadedFileInfo(null);
      setUploadedFileColumns(getMappedColumnsFromConfig(receivedData));

      const currentFollowupType: FollowupType =
        receivedData.serviceType === "GSTIN_TRANSMISSION"
          ? "GST_TRANSMISSION"
          : "INVOICE_FOLLOWUP";
      setFollowupType(currentFollowupType);

      const dynamicColumns = Array.isArray(receivedData.dynamicColumns)
        ? receivedData.dynamicColumns
        : [];
      const attachmentKeys = dynamicColumns
        .map((column: any) => column?.key)
        .filter(
          (value: any): value is string =>
            typeof value === "string" && value.trim().length > 0,
        );
      const attachmentKeyLabels = dynamicColumns.reduce(
        (acc: Record<string, string>, column: any) => {
          if (column?.key) acc[column.key] = column.label || column.key;
          return acc;
        },
        {},
      );

      const logoUrls = [
        receivedData.upload_link_props?.partner1_logo,
        receivedData.upload_link_props?.partner2_logo,
      ].filter(
        (url): url is string =>
          typeof url === "string" && url.trim().length > 0,
      );

      // Prefill basic info
      setBasicInfo({
        name: receivedData.name || "",
        client_id: receivedData.client_id || "",
        fromEmail: receivedData.fromEmail || "",
        fromName: receivedData.fromName || "",
        datasourceType:
          receivedData.booking_data_source === "EMAIL" ? "EMAIL" : "MANUAL",
        databaseType: "Mongo",
        database: receivedData.database || "",
        collection: receivedData.collection || "",
        mailType: receivedData.mailType || "SINGLE",
        bookingid: receivedData.bookingid || "",
        subject: receivedData.subject || "",
        searchPattern: receivedData.searchPattern || "",
        gptContext: receivedData.gptContext || "",
        safety_valve: receivedData.safety_valve || { key: "", value: "" },
        gstconfig: receivedData.gstconfig || {
          email_hotel_gstin: "",
          gstkey: "",
        },
      });

      // Prefill flows
      if (
        receivedData.followup_flow &&
        Array.isArray(receivedData.followup_flow)
      ) {
        const prefilledFlows = receivedData.followup_flow.map(
          (flow: any, index: number) => ({
            id: index + 1,
            type: flow.type || "EMAIL",
            interval: flow.interval || "",
            name: flow.name || "",
            time: flow.time || "",
            templateId: flow.templateId || "",
            intervalAgainst: flow.intervalAgainst || "",
          }),
        );
        setFlows(prefilledFlows);
      }

      const receivedFlows = Array.isArray(receivedData.followup_flow)
        ? receivedData.followup_flow
        : [];
      const entries = (receivedFlows.length > 0 ? receivedFlows : [{}]).map(
        (flow: any, index: number) => ({
          id: index + 1,
          name: flow.name || "",
          interval:
            currentFollowupType === "INVOICE_FOLLOWUP"
              ? String(flow.interval ?? "0")
              : "0",
          time: (() => { const t = flow.time || "00:00"; return t.includes(":") ? t : `${t.padStart(2, "0")}:00`; })(),
          intervalAgainst:
            currentFollowupType === "GST_TRANSMISSION"
              ? flow.interval || ""
              : "",
          templateId: flow.templateId || receivedData.templateId || "",
          subject: flow.subject || receivedData.subject || "",
          logoUrls: index === 0 ? logoUrls : [],
        }),
      );
      setFollowupData({
        entries,
        bulkEmailKey: receivedData.bulkEmailKey || "",
        attachmentKeys,
        attachmentKeyLabels,
        from: receivedData.fromEmail || "",
        fromName: receivedData.fromName || "",
        cc: Array.isArray(receivedData.cc_emails)
          ? receivedData.cc_emails.join(", ")
          : receivedData.cc || "",
      });

      // Prefill mappings
      if (receivedData.mapping && typeof receivedData.mapping === "object") {
        const prefilledMappings: ColumnMapping[] = [];

        // Determine which predefined set to use based on serviceType
        const isEditGST = receivedData.serviceType === "GSTIN_TRANSMISSION";
        const predefinedMappings = isEditGST
          ? GST_MAPPINGS.map((m) => ({
              sourceField: m.sourceField,
              isRequired: m.isRequired ?? false,
            }))
          : INVOICE_MAPPINGS.map((m) => ({
              sourceField: m.sourceField,
              isRequired: m.isRequired ?? false,
            }));

        const resolveTargetField = (dbValue: any): string => {
          if (dbValue === null) return "__null__";
          // Handle legacy records that stored the string "null"/"Null" before actual null was used
          if (dbValue === "null" || dbValue === "Null") return "__null__";
          if (typeof dbValue === "string") return dbValue;
          return "";
        };

        predefinedMappings.forEach((mapping, index) => {
          prefilledMappings.push({
            id: index + 1,
            sourceField: mapping.sourceField,
            targetField: resolveTargetField(receivedData.mapping[mapping.sourceField]),
            isPredefined: true,
            isRequired: mapping.isRequired,
          });
        });

        // Handle any extra mappings if present (dynamic ones)
        const predefinedSourceFields = predefinedMappings.map(
          (m) => m.sourceField,
        );
        Object.entries(receivedData.mapping).forEach(
          ([sourceField, targetField]) => {
            if (!predefinedSourceFields.includes(sourceField)) {
              prefilledMappings.push({
                id: prefilledMappings.length + 1,
                sourceField: sourceField,
                targetField: resolveTargetField(targetField),
                isPredefined: false,
                isRequired: false,
              });
            }
          },
        );

        setMappings(prefilledMappings);
      }

      const uniqueIdColumn =
        receivedData.bookingid ||
        (typeof receivedData.mapping?.email_bookingId === "string"
          ? receivedData.mapping.email_bookingId
          : "");
      setBookingIds(uniqueIdColumn ? [uniqueIdColumn] : []);
    } else {
      setAction("create");
      setEditId(null);
      setConfigClientType(location.state?.clientType || "");
      setConfigWorkspaceName(location.state?.workspaceName || "");
      setConfigWorkspaceId(location.state?.workspaceId || "");
      setConfigTmcDomain(location.state?.tmcDomain || "");
    }
  }, [location]);

  const handleReupload = async (file: File, newColumns: string[]) => {
    setReuploadPendingFile(file);
    setReuploadNewCols(newColumns);

    const loading = message.loading("Checking column compatibility...", 0);
    try {
      const res = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/followupKeys",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
            workspaceId: localStorage.getItem("currentWorkspace") || "",
          },
          body: JSON.stringify({
            db_type: "mongo",
            db: "email_followup_automation",
            table: basicInfo.collection || basicInfo.name,
            collection: basicInfo.collection || basicInfo.name,
          }),
        },
      );
      const data = await res.json();
      loading();
      const rawCols = Array.isArray(data)
        ? data
        : (data.keys ?? data.columns ?? data.fields ?? data.data ?? []);
      const existingCols: string[] = rawCols
        .map((item: any) =>
          typeof item === "string"
            ? item
            : (item.column_name ?? item.name ?? String(item)),
        )
        .filter((c: string) => c !== "_id");
      setReuploadExistingCols(existingCols);
    } catch {
      loading();
      const fallback = mappings
        .map((m) => m.targetField)
        .filter((v): v is string => Boolean(v));
      setReuploadExistingCols(fallback);
    }

    setReuploadModalVisible(true);
  };

  const confirmReupload = async () => {
    if (!reuploadPendingFile) return;
    setReuploadModalVisible(false);

    const loading = message.loading("Uploading file...", 0);
    try {
      const formData = new FormData();
      formData.append("file", reuploadPendingFile);
      formData.append("filename", reuploadPendingFile.name);
      formData.append("fileType", reuploadPendingFile.type);
      formData.append("clientId", basicInfo.client_id || "");

      const response = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/uploadExcel",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            workspaceId: localStorage.getItem("currentWorkspace") || "",
          },
          body: formData,
        },
      );

      const data = await response.json();
      loading();
      if (!response.ok)
        throw new Error(data.message || `Upload failed (${response.status})`);

      setUploadedFile(reuploadPendingFile);
      setUploadedFileInfo({
        message: data.message,
        insertedCount: data.insertedCount,
        filename: reuploadPendingFile.name,
      });
      setUploadedFileColumns(reuploadNewCols);
      message.success(
        data.insertedCount
          ? `${data.message || "Uploaded!"} ${data.insertedCount} records inserted.`
          : data.message || "File uploaded successfully!",
      );
    } catch (error) {
      loading();
      message.error(
        `Failed to upload: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
    setReuploadPendingFile(null);
  };

  const saveConfiguration = async () => {
    try {
      const formattedMappings: Record<string, string | null> = mappings.reduce(
        (acc: Record<string, string | null>, m) => {
          if (m.sourceField && m.targetField) {
            acc[m.sourceField] = m.targetField === "__null__" ? null : m.targetField;
          }
          return acc;
        },
        {},
      );

      const isGST = followupType === "GST_TRANSMISSION";
      const isInvoice = followupType === "INVOICE_FOLLOWUP";

      const formattedFlows = followupData.entries.map((entry) => ({
        type: "EMAIL",
        // Invoice uses a numeric day-offset; GST uses an event-name key
        interval: isGST ? entry.intervalAgainst || null : entry.interval || "0",
        name: entry.name || null,
        time: entry.time || null,
        templateId: entry.templateId || null,
      }));

      const cc_emails = followupData.cc
        ? followupData.cc
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
        : [];

      const firstEntryLogos = followupData.entries[0]?.logoUrls ?? [];

      // bookingid = the raw column that uniquely identifies each record
      const bookingid =
        formattedMappings.email_bookingId || bookingIds[0] || "";

      const basePayload = {
        ...(editId ? { _id: editId } : {}),
        name: basicInfo.name,
        client_id: basicInfo.client_id,
        fromEmail: followupData.from || basicInfo.fromEmail,
        fromName: followupData.fromName || basicInfo.fromName,
        status: "PENDING",
        databaseType: sourceType === "UPLOAD" ? "MONGO" : basicInfo.databaseType,
        mailType: isInvoice ? "BULK" : "SINGLE",
        bookingid,
        gptContext: basicInfo.gptContext,
        safety_valve: basicInfo.safety_valve,
        followup_flow: formattedFlows,
        type: "Hotel",
        cc_emails,
        serviceType: isGST ? "GSTIN_TRANSMISSION" : "INVOICE_FOLLOWUP",
        workspaceId: configWorkspaceId,
        workspaceName: configWorkspaceName,
      };

      let payload: any;

      if (isInvoice) {
        payload = {
          ...basePayload,
          database: "email_followup_automation",
          collection: basicInfo.name,
          "sync-type": "MANUAL",
          include_attachment: true,
          templateId: followupData.entries[0]?.templateId || null,
          subject: followupData.entries[0]?.subject || "",
          bulkEmailKey: followupData.bulkEmailKey || "",
          dynamicColumns: Object.entries(followupData.attachmentKeyLabels).map(
            ([key, label]) => ({ key, label }),
          ),
          groupBy: ["$email_hotel_gstin"],
          mainGroupKey: "email_hotel_gstin",
          followupStatus: "Inactive",
          gstconfig: {
            gstkey: "gstin",
            email_hotel_gstin: "email_hotel_gstin",
          },
          mapping: {
            email_bookingId: "email_bookingId" in formattedMappings ? formattedMappings.email_bookingId : "",
            email_sharableBookingId: "email_sharableBookingId" in formattedMappings ? formattedMappings.email_sharableBookingId : "",
            email_checkindate: "email_checkindate" in formattedMappings ? formattedMappings.email_checkindate : "null",
            email_bookingdate: "email_bookingdate" in formattedMappings ? formattedMappings.email_bookingdate : "null",
            email_checkoutdate: "email_checkoutdate" in formattedMappings ? formattedMappings.email_checkoutdate : "",
            company_name: "company_name" in formattedMappings ? formattedMappings.company_name : "",
            gstin: "gstin" in formattedMappings ? formattedMappings.gstin : "",
            company_address: "company_address" in formattedMappings ? formattedMappings.company_address : "Null",
            email_hotel_contact_email: [],
            email_hotel_contact_phone: [],
            totalGST: "totalGST" in formattedMappings ? formattedMappings.totalGST : "",
            email_hotel_name: "email_hotel_name" in formattedMappings ? formattedMappings.email_hotel_name : "",
            email_hotel_gstin: "email_hotel_gstin" in formattedMappings ? formattedMappings.email_hotel_gstin : "",
          },
        };
      } else {
        // GST_TRANSMISSION
        payload = {
          ...basePayload,
          database: basicInfo.database,
          collection: basicInfo.collection,
          datasetType: "BOOKING",
          booking_data_source: sourceType === "DATABASE" ? "API" : "MANUAL",
          followupType: "HOTEL_BOOKING",
          templateId: followupData.entries[0]?.templateId || null,
          subject: followupData.entries[0]?.subject || "",
          upload_link_props: {
            has_partnership: firstEntryLogos.length > 0,
            partner1_logo: firstEntryLogos[0] ?? "",
            partner2_logo: firstEntryLogos[1] ?? "",
          },
          gstconfig: {
            gstkey: "gstin",
          },
          mapping: {
            email_bookingId: "email_bookingId" in formattedMappings ? formattedMappings.email_bookingId : "",
            email_sharableBookingId: "email_sharableBookingId" in formattedMappings ? formattedMappings.email_sharableBookingId : "",
            email_hotel_name: "email_hotel_name" in formattedMappings ? formattedMappings.email_hotel_name : "",
            email_checkindate: "email_checkindate" in formattedMappings ? formattedMappings.email_checkindate : "",
            email_bookingdate: "email_bookingdate" in formattedMappings ? formattedMappings.email_bookingdate : "",
            email_checkoutdate: "email_checkoutdate" in formattedMappings ? formattedMappings.email_checkoutdate : "",
            email_hotel_contact_email: [],
            email_hotel_contact_phone: [],
            email_hotel_gstin: [],
            company_name: "company_name" in formattedMappings ? formattedMappings.company_name : "",
            gstin: "gstin" in formattedMappings ? formattedMappings.gstin : "",
            company_address: "company_address" in formattedMappings ? formattedMappings.company_address : "",
          },
        };
      }

      if (uploadedFile && uploadedFileInfo) {
        payload.uploadedFile = {
          name: uploadedFile.name,
          size: uploadedFile.size,
          type: uploadedFile.type,
          ...uploadedFileInfo,
        };
      }

      const response = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/addNewConfig",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
            workspaceId: localStorage.getItem("currentWorkspace") || "",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error saving configuration:", error);
      throw error;
    }
  };

  const handleNext = async () => {
    if (isEditMode) return;

    const { name, client_id, databaseType, database, collection } = basicInfo;

    if (!name || !client_id) {
      message.error("Please fill all required basic information fields.");
      return;
    }

    const checkLoading = message.loading("Checking client ID...", 0);
    try {
      const checkRes = await fetch(
        "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/checkClientId",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
            workspaceId: localStorage.getItem("currentWorkspace") || "",
          },
          body: JSON.stringify({ client_id }),
        },
      );
      const checkData = await checkRes.json();
      checkLoading();
      if (checkData.exists) {
        message.error(
          `Client ID "${client_id}" already exists. Please use a different ID.`,
        );
        return;
      }
    } catch (err) {
      checkLoading();
      message.error("Failed to validate client ID. Please try again.");
      return;
    }

    if (!sourceType) {
      message.error("Please select a source type.");
      return;
    }

    if (sourceType === "DATABASE") {
      if (!databaseType || !database || !collection) {
        message.error(
          "Please fill in DB Type, Database Name, and Collection/Table Name.",
        );
        return;
      }
      const loading = message.loading("Fetching table columns...", 0);
      try {
        const res = await fetch(
          "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/followupKeys",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
              workspaceId: localStorage.getItem("currentWorkspace") || "",
            },
            body: JSON.stringify({
              db_type: DB_TYPE_MAP[databaseType] ?? databaseType.toLowerCase(),
              db: database,
              table: collection,
            }),
          },
        );
        const data = await res.json();
        loading();
        if (!res.ok) throw new Error(data.message || `Failed (${res.status})`);
        const rawCols = Array.isArray(data)
          ? data
          : (data.keys ?? data.columns ?? data.fields ?? data.data ?? []);
        const cols: string[] = rawCols.map((item: any) =>
          typeof item === "string"
            ? item
            : (item.column_name ?? item.name ?? String(item)),
        );
        setUploadedFileColumns(cols);
      } catch (err) {
        loading();
        message.error(
          `Failed to fetch columns: ${err instanceof Error ? err.message : "Unknown error"}`,
        );
        return;
      }
    } else {
      if (!uploadedFile) {
        message.error("Please upload a file to proceed.");
        return;
      }
    }

    setActiveTab("dataMapping");
  };

  const handleSave = () => {
    if (isEditMode) return;

    // Validate column mappings
    if (bookingIds.length === 0) {
      message.error(
        "Please select at least one Unique ID column in Data Mapping.",
      );
      return;
    }

    const missingPredefined = mappings.some(
      (m) => m.isPredefined && !m.targetField,
    );
    if (missingPredefined) {
      message.error("Please complete all predefined column mappings.");
      return;
    }

    // Validate scheduler
    if (!followupData.from) {
      message.error("Please fill in the From email address in the Scheduler.");
      return;
    }

    const missingEntry = followupData.entries.some(
      (e) => !e.templateId || !e.subject,
    );
    if (missingEntry) {
      message.error(
        "Please fill in Template ID and Subject for all followup entries.",
      );
      return;
    }

    const loadingMessage = message.loading("Saving configuration...", 0);

    saveConfiguration()
      .then(() => {
        loadingMessage();
        message.success("Configuration saved successfully!");
        setTimeout(() => navigate(-1), 1000);
      })
      .catch((error) => {
        loadingMessage();
        message.error("Failed to save configuration: " + error.message);
      });
  };

  const removeMapping = (idToRemove: number) => {
    setMappings((prev) => prev.filter((mapping) => mapping.id !== idToRemove));
  };

  const handleMappingChange = (
    id: number,
    field: keyof ColumnMapping,
    value: string,
  ) => {
    const updated = mappings.map((m) =>
      m.id === id ? { ...m, [field]: value } : m,
    );
    setMappings(updated);
  };

  const addMappingPair = () => {
    const newId = Date.now(); // safe enough
    setMappings((prev) => [
      ...prev,
      {
        id: newId,
        sourceField: "",
        targetField: "",
        isPredefined: false,
        isRequired: false,
      },
    ]);
  };

  const { TabPane } = Tabs;

  const FOLLOWUP_TYPES = [
    {
      key: "GST_TRANSMISSION" as FollowupType,
      label: "GST Transmission",
      description: "Send GST-related followup emails automatically.",
      icon: <FileTextOutlined />,
    },
    {
      key: "INVOICE_FOLLOWUP" as FollowupType,
      label: "Invoice Followup",
      description: "Send invoice reminders to guests after their stay.",
      icon: <MailOutlined />,
    },
  ];

const handleFollowupTypeChange = (type: FollowupType) => {
  setFollowupType(type);
  setMappings(type === "INVOICE_FOLLOWUP" ? INVOICE_MAPPINGS : GST_MAPPINGS);
  setFollowupData({
    entries: [{
      id: 1,
      name: "",
      interval: type === "INVOICE_FOLLOWUP" ? "1" : "0",
      time: "00:00",
      intervalAgainst: "",
      templateId: type === "INVOICE_FOLLOWUP" ? "d-a15d95284cb346888a799afb0976e302" : "",
      subject: type === "INVOICE_FOLLOWUP" ? "Urgent: Please Re-Share Invoices Issued By {{email_hotel_name}} for {{company_name}}" : "",
      logoUrls: [],
    }],
    bulkEmailKey: "",
    attachmentKeys: [],
    attachmentKeyLabels: {},
    from: "",
    fromName: "",
    cc: "",
  });
};

  return (
    <div className="HotelMailFollowupConfig">
      <PageHeader
        title={isEditMode ? "Edit Configuration" : "New Configuration Setup"}
      />
      <div className="ContentContainer">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            if (key === "dataMapping") {
              handleNext();
            } else {
              setActiveTab(key);
            }
          }}
          className="config-tabs"
        >
          <TabPane tab="Config" key="config">
            {/* Step 1 — Followup type */}
            <div style={{ marginBottom: 32 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                  borderLeft: "3px solid #0a8394",
                  paddingLeft: 12,
                }}
              >
                <Typography variant="h5" weight={600}>
                  Select Followup Type
                </Typography>
              </div>
              <div className="source-type-cards">
                {FOLLOWUP_TYPES.map((t) => (
                  <div
                    key={t.key as string}
                    className={`source-card${followupType === t.key ? " selected" : ""}${isEditMode ? " disabled" : ""}`}
                    onClick={() => { if (!isEditMode) handleFollowupTypeChange(t.key); }}
                  >
                    <div className="source-card-icon">{t.icon}</div>
                    <div>
                      <Typography variant="body" weight={600}>
                        {t.label}
                      </Typography>
                      <Typography variant="xs" style={{ color: "#595959" }}>
                        {t.description}
                      </Typography>
                    </div>
                    <div className="source-card-radio">
                      <div
                        className={
                          followupType === t.key
                            ? "radio-filled"
                            : "radio-empty"
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <InformationFlowTab
              basicInfo={basicInfo}
              setBasicInfo={setBasicInfo}
              sourceType={sourceType}
              setSourceType={setSourceType}
              uploadedFile={uploadedFile}
              setUploadedFile={setUploadedFile}
              setUploadedFileInfo={setUploadedFileInfo}
              setUploadedFileColumns={setUploadedFileColumns}
              readOnly={isEditMode}
              onReupload={isEditMode ? handleReupload : undefined}
            />

            {followupType && (
              <EmailFollowupTab
                followupType={followupType}
                followupData={followupData}
                setFollowupData={setFollowupData}
                fileColumns={uploadedFileColumns}
                disabled={isEditMode}
              />
            )}
          </TabPane>
          <TabPane tab="Data Mapping" key="dataMapping" disabled={isEditMode}>
            <ColumnMappingTab
              mappings={mappings}
              sourceFieldPlaceholders={sourceFieldPlaceholders}
              addMappingPair={addMappingPair}
              removeMapping={removeMapping}
              handleMappingChange={handleMappingChange}
              fileColumns={uploadedFileColumns}
              bookingIds={bookingIds}
              onBookingIdsChange={setBookingIds}
              disabled={isEditMode}
            />
          </TabPane>
        </Tabs>

        <div className="form-footer">
          {!isEditMode && activeTab === "config" && (
            <Button type="primary" onClick={handleNext} className="save-button">
              Next
            </Button>
          )}
          {!isEditMode && activeTab === "dataMapping" && (
            <Button type="primary" onClick={handleSave} className="save-button">
              Save
            </Button>
          )}
        </div>
      </div>
      <Modal
        title="Column Compatibility Check"
        open={reuploadModalVisible}
        onCancel={() => {
          setReuploadModalVisible(false);
          setReuploadPendingFile(null);
        }}
        footer={(() => {
          const isExactMatch =
            reuploadNewCols.length === reuploadExistingCols.length &&
            reuploadNewCols.every((c) => reuploadExistingCols.includes(c));
          return [
            <Button
              key="cancel"
              onClick={() => {
                setReuploadModalVisible(false);
                setReuploadPendingFile(null);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="confirm"
              type="primary"
              disabled={!isExactMatch}
              onClick={confirmReupload}
            >
              Proceed with Upload
            </Button>,
          ];
        })()}
        width={600}
      >
        {(() => {
          const added = reuploadNewCols.filter(
            (c) => !reuploadExistingCols.includes(c),
          );
          const removed = reuploadExistingCols.filter(
            (c) => !reuploadNewCols.includes(c),
          );
          const matched = reuploadNewCols.filter((c) =>
            reuploadExistingCols.includes(c),
          );
          const isExactMatch = added.length === 0 && removed.length === 0;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ fontSize: 13, color: "#595959" }}>
                New file: <strong>{reuploadNewCols.length}</strong> columns
                &nbsp;|&nbsp; Existing:{" "}
                <strong>{reuploadExistingCols.length}</strong> columns
              </div>

              {isExactMatch ? (
                <div
                  style={{
                    background: "#f6ffed",
                    border: "1px solid #b7eb8f",
                    borderRadius: 8,
                    padding: "10px 14px",
                    color: "#389e0d",
                    fontWeight: 600,
                  }}
                >
                  All columns match — you can proceed with the upload.
                </div>
              ) : (
                <div
                  style={{
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 8,
                    padding: "10px 14px",
                    color: "#d46b08",
                    fontWeight: 600,
                  }}
                >
                  Columns do not match 100%. Fix your file before uploading.
                </div>
              )}

              {removed.length > 0 && (
                <div
                  style={{
                    background: "#fff1f0",
                    border: "1px solid #ffa39e",
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#cf1322",
                      marginBottom: 8,
                    }}
                  >
                    Missing from new file ({removed.length}) — these columns
                    must be present
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {removed.map((c) => (
                      <Tag key={c} color="error">
                        {c}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {added.length > 0 && (
                <div
                  style={{
                    background: "#fff1f0",
                    border: "1px solid #ffa39e",
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#cf1322",
                      marginBottom: 8,
                    }}
                  >
                    Extra columns not in existing ({added.length}) — remove
                    these from your file
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {added.map((c) => (
                      <Tag key={c} color="error">
                        {c}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {matched.length > 0 && (
                <div
                  style={{
                    background: "#fafafa",
                    border: "1px solid #e8e8e8",
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#262626",
                      marginBottom: 8,
                    }}
                  >
                    Matched columns ({matched.length})
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {matched.map((c) => (
                      <Tag key={c}>{c}</Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default HotelEmailConfigSetup;
